/**
 * DLB-2 — revision ordering for bulk-delete-older-revisions.
 *
 * The Document Library groups same-`contentHash` documents into a
 * single canonical row (DocumentLibrary.tsx `revisionGroupsByCanonical`).
 * The bulk-delete-older-revisions action needs to identify, for each
 * group, which doc is the "newest" so it can be kept while the rest
 * are tombstoned + deleted.
 *
 * Strategy (in priority order):
 *
 *   1. If the group exposes an explicit version chain via
 *      `versionChain.replaces` (one doc says "I replace docId X"), the
 *      newest is the doc with NO successor — the leaf of the chain.
 *      We tolerate forward-looking schema additions: `versionChain` is
 *      typed loosely so we don't break existing call sites that don't
 *      populate it.
 *
 *   2. If no explicit chain (the common case today — pre-DLB documents
 *      have no chain stamps), fall back to the latest `uploadedAt`
 *      timestamp (parseable ISO string). The same heuristic the
 *      DocumentLibrary uses to pick the canonical row in the first
 *      place, so this stays consistent with what the user already sees.
 *
 *   3. Ties on `uploadedAt` (or missing on all members) are broken by
 *      stable iteration order — pick the first member. Deterministic
 *      under all inputs so the bulk-delete preview matches the
 *      ultimate deletion target.
 *
 * Pure helper — no DOM, no Firestore, no store. Importable from tests
 * and from the DocumentLibrary modal selector identically.
 */

/**
 * Loose duck-typed input shape so the helper composes with both
 * `SiteDocument` (the current type) and any test fixture / future
 * variant. We only need `id`, optional `uploadedAt`, and optional
 * `versionChain.replaces`.
 */
export interface RevisionCandidate {
  id: string;
  uploadedAt?: string;
  versionChain?: {
    replaces?: string;
  };
}

/**
 * Pick the newest member of a revision group. The group is assumed to
 * be docs that share a `contentHash` (or are otherwise considered
 * versions of the same logical document). All members must already be
 * pre-filtered upstream — the helper does NOT validate that.
 *
 * Always returns a defined member when the group is non-empty.
 * Throws on empty input — that's a caller bug, not a runtime case
 * worth swallowing (would surface as a silent "everyone got
 * tombstoned" data-loss event in production).
 */
export function pickNewestRevision<T extends RevisionCandidate>(group: T[]): T {
  if (group.length === 0) {
    throw new Error('pickNewestRevision: empty group — caller must pre-filter');
  }
  if (group.length === 1) return group[0]!;

  // Strategy 1: explicit version chain. Build a `successor` set; the
  // newest is whoever has NO successor inside this group.
  const idsInGroup = new Set(group.map(d => d.id));
  const hasSuccessor = new Set<string>();
  for (const d of group) {
    const replaces = d.versionChain?.replaces;
    // Only count successor edges that point to members of THIS group.
    // A `replaces` arrow to a doc outside the group (e.g. a cross-site
    // reference) is ignored — it doesn't tell us anything about
    // within-group ordering.
    if (replaces && idsInGroup.has(replaces)) {
      hasSuccessor.add(replaces);
    }
  }
  const noSuccessor = group.filter(d => !hasSuccessor.has(d.id));
  if (noSuccessor.length === 1) {
    return noSuccessor[0]!;
  }
  // Multiple leaves (branched or no chain stamps) → fall through to
  // timestamp tie-break across the leaves only.
  const tieBreakSet = noSuccessor.length > 0 ? noSuccessor : group;

  // Strategy 2: latest uploadedAt wins. `localeCompare` on ISO strings
  // is lexicographic = chronological for valid ISO 8601, and degrades
  // gracefully to "" vs "" (equal) for missing values. Using
  // `localeCompare` instead of Date.parse keeps the result
  // deterministic even on malformed timestamps where parse would
  // return NaN.
  const sorted = [...tieBreakSet].sort((a, b) =>
    (b.uploadedAt ?? '').localeCompare(a.uploadedAt ?? ''),
  );
  return sorted[0]!;
}

/**
 * Convenience: given a revision group, return the ids of every member
 * EXCEPT the newest. These are the candidates for bulk-delete-older
 * revisions.
 */
export function olderRevisionIds<T extends RevisionCandidate>(group: T[]): string[] {
  if (group.length <= 1) return [];
  const newest = pickNewestRevision(group);
  return group.filter(d => d.id !== newest.id).map(d => d.id);
}
